// ─────────────────────────────────────────────────────────────────────────
// CANARY OUTCOMES PANEL  (System tab)
//
// Durable daily record of the two canary/shadow pipelines that back the
// promote-to-production decision:
//   1. Signal-gen v2 — PARITY. Each night the shadow builds v2's order book and
//      compares it to v1's real staged orders. Success = MATCH (v2 == v1); a
//      mismatch is a bug to investigate, not an improvement. A green match-streak
//      is what gates the SIGNALGEN_V2_LIVE cutover.
//   2. Order-lifecycle v2 — NEW-capture + zero-regression. Daily counts from the
//      always-on OrderEvent log (events, error/reject callbacks, distinct permIds)
//      + NettedOrder rejects. `enabled` flags whether the ORDER_LIFECYCLE_V2
//      behavioral paths were active that day (Paper canary).
//
// Read-only. Data starts accruing after the batch deploys + the canary flags flip.
// Mobile-first, dark-default, institutional styling.
// ─────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';
import { tradingService } from '../../../services/tradingService';
import { useSortableRows, SortableTh } from '../../../components/tables/SortableTable';

const Spinner = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
  </svg>
);

const num = (v) => (v == null ? '—' : Number(v).toLocaleString());

function MatchBadge({ match }) {
  if (match === true) return <span className="text-emerald-600 dark:text-emerald-400 font-semibold">✓ match</span>;
  if (match === false) return <span className="text-red-600 dark:text-red-400 font-semibold">✗ mismatch</span>;
  return <span className="text-gray-400 dark:text-gray-500" title="no v1 staged baseline to compare">— n/a</span>;
}

// ── Signal-gen v2 parity table ──
function V2ParityTable({ rows }) {
  const cols = [
    { key: 'date', type: 'string', get: (r) => r.date },
    { key: 'account', type: 'string', get: (r) => r.account_name || '' },
    { key: 'match', type: 'number', get: (r) => (r.match === true ? 1 : r.match === false ? -1 : 0) },
    { key: 'v1', type: 'number', get: (r) => r.v1_orders ?? 0 },
    { key: 'v2', type: 'number', get: (r) => r.v2_orders ?? 0 },
    { key: 'diffs', type: 'number', get: (r) => r.n_diffs ?? 0 },
  ];
  const { sorted, toggle, indicator } = useSortableRows(rows, cols, { key: 'date', dir: 'desc' });
  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
            <SortableTh label="Date" colKey="date" toggle={toggle} indicator={indicator} align="left" />
            <SortableTh label="Account" colKey="account" toggle={toggle} indicator={indicator} align="left" />
            <SortableTh label="Parity" colKey="match" toggle={toggle} indicator={indicator} align="left" />
            <SortableTh label="v1" colKey="v1" toggle={toggle} indicator={indicator} />
            <SortableTh label="v2" colKey="v2" toggle={toggle} indicator={indicator} />
            <SortableTh label="Diffs" colKey="diffs" toggle={toggle} indicator={indicator} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr key={`${r.date}-${r.account_id}-${i}`} className="border-b border-gray-100 dark:border-gray-800/60">
              <td className="py-1.5 pr-3 font-mono text-gray-700 dark:text-gray-300">{r.date}</td>
              <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-300">{r.account_name || '—'}</td>
              <td className="py-1.5 pr-3"><MatchBadge match={r.match} /></td>
              <td className="py-1.5 pr-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{num(r.v1_orders)}</td>
              <td className="py-1.5 pr-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{num(r.v2_orders)}</td>
              <td className={`py-1.5 pr-3 text-right tabular-nums ${(r.n_diffs || 0) > 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-400'}`}>{num(r.n_diffs)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Order-lifecycle canary table ──
function OrderLifecycleTable({ rows }) {
  const cols = [
    { key: 'date', type: 'string', get: (r) => r.date },
    { key: 'account', type: 'string', get: (r) => r.account_name || '' },
    { key: 'enabled', type: 'number', get: (r) => (r.enabled ? 1 : 0) },
    { key: 'events', type: 'number', get: (r) => r.order_events ?? 0 },
    { key: 'errors', type: 'number', get: (r) => r.errors ?? 0 },
    { key: 'permids', type: 'number', get: (r) => r.permids_seen ?? 0 },
    { key: 'rejected', type: 'number', get: (r) => r.netted_rejected ?? 0 },
  ];
  const { sorted, toggle, indicator } = useSortableRows(rows, cols, { key: 'date', dir: 'desc' });
  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
            <SortableTh label="Date" colKey="date" toggle={toggle} indicator={indicator} align="left" />
            <SortableTh label="Account" colKey="account" toggle={toggle} indicator={indicator} align="left" />
            <SortableTh label="Flag" colKey="enabled" toggle={toggle} indicator={indicator} align="left" />
            <SortableTh label="Events" colKey="events" toggle={toggle} indicator={indicator} />
            <SortableTh label="Errors" colKey="errors" toggle={toggle} indicator={indicator} />
            <SortableTh label="PermIDs" colKey="permids" toggle={toggle} indicator={indicator} />
            <SortableTh label="Rejected" colKey="rejected" toggle={toggle} indicator={indicator} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr key={`${r.date}-${r.account_id}-${i}`} className="border-b border-gray-100 dark:border-gray-800/60">
              <td className="py-1.5 pr-3 font-mono text-gray-700 dark:text-gray-300">{r.date}</td>
              <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-300">{r.account_name || '—'}</td>
              <td className="py-1.5 pr-3">
                {r.enabled
                  ? <span className="text-emerald-600 dark:text-emerald-400 font-semibold">ON</span>
                  : <span className="text-gray-400 dark:text-gray-500">off</span>}
              </td>
              <td className="py-1.5 pr-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{num(r.order_events)}</td>
              <td className={`py-1.5 pr-3 text-right tabular-nums ${(r.errors || 0) > 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-400'}`}>{num(r.errors)}</td>
              <td className="py-1.5 pr-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{num(r.permids_seen)}</td>
              <td className={`py-1.5 pr-3 text-right tabular-nums ${(r.netted_rejected || 0) > 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-400'}`}>{num(r.netted_rejected)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CanaryOutcomesPanel() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [days, setDays]       = useState(21);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tradingService.getCanaryOutcomes(days);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sg = data?.signalgen_v2 || { rows: [], match_streak: 0 };
  const ol = data?.order_lifecycle || { rows: [] };
  const streak = sg.match_streak || 0;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#0d1117] p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-serif text-gray-900 dark:text-gray-100">Canary Outcomes</h3>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            Daily v2 parity + order-lifecycle canary — the promote-to-prod evidence.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="text-[11px] bg-transparent border border-gray-300 dark:border-gray-700 rounded px-1.5 py-1 text-gray-700 dark:text-gray-300"
          >
            <option value={7}>7d</option>
            <option value={21}>21d</option>
            <option value={60}>60d</option>
          </select>
          <button
            onClick={fetchData}
            disabled={loading}
            className="text-[11px] px-2 py-1 rounded border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-gold/50 disabled:opacity-50 inline-flex items-center gap-1"
          >
            {loading ? <Spinner /> : '↻'} Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="text-[11px] text-red-600 dark:text-red-400 mb-2">Error: {error}</div>
      )}

      {/* ── Signal-gen v2 parity ── */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Signal-gen v2 · parity</span>
          <span
            className={`text-[11px] px-1.5 py-0.5 rounded font-semibold ${
              streak >= 3
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
            }`}
            title="Consecutive most-recent days where every account's v2 book matched v1"
          >
            {streak} match-night{streak === 1 ? '' : 's'}
          </span>
        </div>
        {sg.rows.length === 0 ? (
          <div className="text-[11px] text-gray-400 dark:text-gray-500 py-2">
            No parity data yet — starts accruing after deploy + <span className="font-mono">SIGNALGEN_V2_SHADOW_ENABLED</span>.
          </div>
        ) : (
          <V2ParityTable rows={sg.rows} />
        )}
      </div>

      {/* ── Order-lifecycle canary ── */}
      <div>
        <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
          Order-lifecycle v2 · canary
        </div>
        {ol.rows.length === 0 ? (
          <div className="text-[11px] text-gray-400 dark:text-gray-500 py-2">
            No capture yet — the nightly beat records once deployed (Paper flag flip shows as <span className="text-emerald-600 dark:text-emerald-400">ON</span>).
          </div>
        ) : (
          <OrderLifecycleTable rows={ol.rows} />
        )}
      </div>
    </div>
  );
}
